/**
 * Hooks de catálogo asignable (US-2.4, TG-53).
 *
 * Solo se ofrece lo que el backend aceptaría:
 * - Packs `ACTIVE` **y vigentes** (`validAt` = ahora; el backend además valida al
 *   asignar y responde `PACK_NOT_ASSIGNABLE` si cambió mientras el modal estaba abierto).
 * - Membresías con `active = true`.
 *
 * La consulta se difiere hasta que el modal se abre (`enabled`), reutilizando los
 * clientes de los catálogos existentes (`packApi`/`membershipApi`).
 */
import { useCallback, useEffect, useState } from 'react';
import type { EnergyPack, Membership } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { packApi } from '@/features/packs/api/packApi';
import { membershipApi } from '@/features/membresias/api/membershipApi';

/** Tope de elementos por consulta (máximo del contrato: 100). */
export const ASSIGNABLE_CATALOG_LIMIT = 100;

export interface AssignableCatalogResult<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

/** `true` si el pack está dentro de su ventana de vigencia en el instante dado. */
export function isPackCurrentlyValid(pack: EnergyPack, now: Date = new Date()): boolean {
  if (pack.status !== 'ACTIVE') return false;
  const from = new Date(pack.validFrom).getTime();
  if (Number.isNaN(from) || from > now.getTime()) return false;
  if (!pack.validTo) return true;
  const to = new Date(pack.validTo).getTime();
  return !Number.isNaN(to) && to > now.getTime();
}

export function useAssignablePacks(
  enabled: boolean,
): AssignableCatalogResult<EnergyPack> {
  const [items, setItems] = useState<EnergyPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    const now = new Date();

    packApi
      .list({
        status: 'ACTIVE',
        validAt: now.toISOString(),
        offset: 0,
        limit: ASSIGNABLE_CATALOG_LIMIT,
      })
      .then((page) => {
        if (cancelled) return;
        setItems(page.data.filter((pack) => isPackCurrentlyValid(pack, now)));
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(caught, 'No se pudo cargar el catálogo de packs.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { items, loading, error, refresh };
}

export function useAssignableMemberships(
  enabled: boolean,
): AssignableCatalogResult<Membership> {
  const [items, setItems] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    membershipApi
      .list({
        status: 'ACTIVE',
        offset: 0,
        limit: ASSIGNABLE_CATALOG_LIMIT,
      })
      .then((page) => {
        if (cancelled) return;
        setItems(page.data.filter((membership) => membership.active));
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(caught, 'No se pudo cargar el catálogo de membresías.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { items, loading, error, refresh };
}
